import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FileSpreadsheet, FileText, Loader2, Search } from 'lucide-react';
import { OrgPortalApi } from '@/services/org.api';
import { OrgReportsApi } from '@/services/org-reports.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';

const ROLE_OPTIONS = ['owner', 'admin', 'manager', 'school_admin', 'teacher', 'head_faculty', 'staff', 'caretaker', 'cashier'];

/**
 * Teachers Attendance Summary — org tier, read-only. Per-school
 * total_teachers/total_present/total_absent for a date range, matching
 * the real `state-reports.teachers-attendance-summary-report` shape.
 * `absent` here means "no present mark in range" (total teachers minus
 * total present), same convention real legacy uses — not an explicit
 * absent count. Defaults to month-to-date (1st of this month through
 * today), same as real legacy's own default when no date is picked.
 */
export function OrgTeachersAttendanceSummaryPage() {
  const [schoolId, setSchoolId] = useState('');
  const [stateId, setStateId] = useState<number | ''>('');
  const [status, setStatus] = useState<'active' | 'inactive' | 'all'>('active');
  const [role, setRole] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-org-teachers-attendance-summary'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: states = [] } = useQuery({
    queryKey: ['org-report-states'],
    queryFn: () => OrgReportsApi.listStates(),
  });

  const params = {
    schoolId: schoolId || undefined,
    startDate,
    endDate,
    stateId: stateId === '' ? undefined : stateId,
    status,
    role: role || undefined,
    showDeleted: showDeleted || undefined,
    search: search.trim() || undefined,
  };

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['org-teachers-attendance-summary', params],
    queryFn: () => OrgReportsApi.teachersAttendanceSummary(params),
  });

  const totals = rows.reduce(
    (acc, r) => ({
      totalTeachers: acc.totalTeachers + r.totalTeachers,
      totalPresent: acc.totalPresent + r.totalPresent,
      totalAbsent: acc.totalAbsent + r.totalAbsent,
    }),
    { totalTeachers: 0, totalPresent: 0, totalAbsent: 0 },
  );

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportTeachersAttendanceSummary(format, params);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Teachers Attendance Summary"
        description="Total teachers, present, and absent per school, over a date range."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by school name, email, phone…"
            className="w-64 pl-9"
          />
        </div>
        <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-48"
          value={stateId}
          onChange={(e) => setStateId(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">All states</option>
          {states.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select className="!w-36" value={status} onChange={(e) => setStatus(e.target.value as 'active' | 'inactive' | 'all')}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="all">All</option>
        </Select>
        <Select className="!w-36" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">All roles</option>
          {ROLE_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>
        <Input type="date" className="!w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="text-sm text-slate-400">to</span>
        <Input type="date" className="!w-40" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
          Show Deleted
        </label>
      </div>

      <div className="mb-4 grid grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="text-xs uppercase tracking-wide text-slate-400">Total Teachers</div>
          <div className="mt-1 text-2xl font-semibold text-slate-900">{totals.totalTeachers}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs uppercase tracking-wide text-slate-400">Present</div>
          <div className="mt-1 text-2xl font-semibold text-green-600">{totals.totalPresent}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs uppercase tracking-wide text-slate-400">Absent</div>
          <div className="mt-1 text-2xl font-semibold text-red-600">{totals.totalAbsent}</div>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">School</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Total Teachers</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Present</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Absent</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isFetching && rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-slate-400">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                  No schools found.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.schoolId}>
                  <td className="px-4 py-3 font-medium text-slate-900">{r.schoolName}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{r.totalTeachers}</td>
                  <td className="px-4 py-3 text-right">
                    <Badge tone="green">{r.totalPresent}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Badge tone="red">{r.totalAbsent}</Badge>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
